/**
 * Google Ads + GA4 Enhanced Ecommerce tracking helper.
 *
 * Fires standard ecommerce events that power:
 *   - Google Ads conversion tracking (purchase)
 *   - Google Ads dynamic remarketing audiences (view_item, add_to_cart, begin_checkout)
 *   - GA4-style ecommerce reports (if GA4 is later linked)
 *
 * The Google tag (gtag.js) for AW-18098930989 is loaded globally in
 * public/index.html. This module assumes window.gtag is available;
 * all calls are no-ops if it is not (SSR, ad-blockers, etc.).
 */

const AW_ID = 'AW-18098930989';

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const buildItem = (product) => {
  if (!product) return null;
  return {
    // Google Ads dynamic remarketing requires id matching the Merchant Center feed
    id: product.sku,
    google_business_vertical: 'retail',
    // GA4-style fields (harmless extras, useful if GA4 is added later)
    item_id: product.sku,
    item_name: product.name_it || product.name_en || product.sku,
    item_brand: product.brand || undefined,
    item_category: product.category || undefined,
    price: safeNumber(product.price),
    quantity: safeNumber(product.quantity || 1),
  };
};

const fire = (eventName, params) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', eventName, params);
  } catch (e) {
    // Never let analytics break the UI
  }
};

/**
 * Fired when a user lands on a product detail page.
 * Powers "Visualizzazioni prodotto" remarketing audience in Google Ads.
 */
export const trackViewItem = (product) => {
  const item = buildItem(product);
  if (!item) return;
  fire('view_item', {
    send_to: AW_ID,
    currency: 'EUR',
    value: item.price,
    items: [item],
  });
};

/**
 * Fired when a user clicks "Aggiungi al carrello".
 * Powers "Aggiunti al carrello ma non acquistato" remarketing audience.
 */
export const trackAddToCart = (product, quantity = 1) => {
  const item = buildItem({ ...product, quantity });
  if (!item) return;
  const value = item.price * safeNumber(quantity);
  fire('add_to_cart', {
    send_to: AW_ID,
    currency: 'EUR',
    value,
    items: [item],
  });
};

/**
 * Fired when a user reaches the checkout page (Step 1 of checkout).
 * Powers "Checkout iniziato ma non completato" remarketing audience.
 */
export const trackBeginCheckout = (cartItems, totalValue) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return;
  const items = cartItems.map(buildItem).filter(Boolean);
  fire('begin_checkout', {
    send_to: AW_ID,
    currency: 'EUR',
    value: safeNumber(totalValue),
    items,
  });
};
